import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { first } from 'rxjs';
import JSZip from 'jszip';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import {
  FilePreviewModalComponent,
  FilePreviewData,
} from '@shared/components/file-preview-modal/file-preview-modal.component';
import { extractFileExtension, resolveFilePreviewType } from '@shared/utils/file-preview.util';
import { SharedModule } from '@shared/shared.module';
import { extractApiErrorMessage } from '../../utils/api-error-message';
import {
  OnboardingCompanyInformation,
  OnboardingProcessStage,
  OnboardingProcessStageRequiredDocuments,
  OnboardingProcessStageSubmittedDocuments,
} from '@interfaces/a-organizar/onboardingCompanyInformation';

const STAGE_LABELS: Record<string, string> = {
  CompanyInformation: 'Informações da empresa',
  Contacts: 'Contatos',
  CompanyDocuments: 'Documentos da empresa',
  GeneralDocuments: 'Documentos gerais',
  BillingDocuments: 'Documentos de faturamento',
  FinancialStatements: 'Demonstrações financeiras',
  Projects: 'Projetos',
  GuaranteeDocuments: 'Documentos de garantia',
};

const DOCUMENT_LABELS: Record<string, string> = {
  ArticlesOfIncorporationAndAmendments: 'Contrato Social ou Estatuto',
  LegalRepresentativesDocumentation: 'Documentação dos representantes legais',
  PartnersIncomeTax: 'Imposto de renda dos sócios',
  CorporateStructureMap: 'Mapa societário',
  ExecutiveOrganogram: 'Organograma executivo',
  HoldingCompanyArticlesOfIncorporation: 'Contrato social da holding',
  FinancialStatements3YearsForHolding: 'Demonstrações financeiras da holding',
  MarketStudies: 'Estudos de mercado',
  InstitutionalPresentation: 'Apresentação institucional',
  CommercialPresentations: 'Apresentações comerciais',
  ProductServiceInformation: 'Informações de produtos e serviços',
  CompanyProductServiceImages: 'Imagens da empresa, produtos e serviços',
  BrandManual: 'Manual da marca',
  LogosVectorJpgPng: 'Logos em vetor, JPG ou PNG',
  BrandPitchDeck: 'Pitch deck da marca',
  AnnualBilling: 'Faturamento anual',
  CurrentYearPartialBilling: 'Faturamento parcial do ano corrente',
  AnnualBillingPreviousYears: 'Faturamento anual de anos anteriores',
  BalanceSheetIncomeStatementCashFlow: 'Balanço, DRE e fluxo de caixa',
  MonthlyBalanceSheetsLast3Years: 'Balancetes mensais dos últimos 3 anos',
  BusinessPlan: 'Plano de negócios',
  Projections: 'Projeções',
  MultiYearFinancialProjection: 'Projeção financeira plurianual',
  BacklogWithBalanceAndProjection: 'Backlog com saldo e projeção',
  Indebtedness: 'Endividamento',
  IndebtednessProjection: 'Projeção de endividamento',
  LoanContracts: 'Contratos de empréstimo',
  BalanceSheetIncomeStatementCashFlowPreviousYears:
    'Balanço, DRE e fluxo de caixa de anos anteriores',
  PublicNotice: 'Edital',
  PhysicalFinancialSchedule: 'Cronograma físico-financeiro',
  ConsortiumConstitutionDocument: 'Documento de constituição do consórcio',
  GeneralInformation: 'Informações gerais',
  RealEstateRegistrationsAndAppraisals: 'Matrícula do imóvel',
  AccountsReceivable: 'Contas a receber',
  CustomerContracts: 'Contratos com clientes',
};

@Component({
  selector: 'app-onboarding-v2-view-modal',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './onboarding-v2-view-modal.component.html',
  styleUrl: './onboarding-v2-view-modal.component.scss',
})
export class OnboardingV2ViewModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  private readonly dialogService = inject(DialogService);
  private readonly confirmationService = inject(ConfirmationService);

  /** Ids em remoção no momento — desabilita a lixeira e troca o ícone por spinner. */
  private readonly removingIds = new Set<string>();

  data: OnboardingCompanyInformation | null = null;
  isDataLoading = false;
  isDownloading = false;
  openSections = new Set<number>([0]);

  constructor(
    translateService: TranslateService,
    private readonly onboardingService: OnboardingService,
    private readonly toastService: ToastHelperService
  ) {
    super(translateService);
  }

  open(companyId: string): void {
    this.data = null;
    this.isDataLoading = true;
    this.openSections = new Set<number>([0]);
    this.dialogRef?.open('', '', '', 'Fechar', '', 'onboarding-v2-view', false, false);

    this.onboardingService.getOnboardingComponyInformation(companyId).subscribe({
      next: data => {
        this.data = data;
        this.isDataLoading = false;
        this.scrollModalContentToTop();
      },
      error: () => {
        this.toastService.showToastError('Erro ao carregar dados do onboarding');
        this.isDataLoading = false;
        this.close();
      },
    });
  }

  close(): void {
    this.dialogRef?.close();
  }

  /**
   * Rótulo do arquivo enviado, no formato "nome.ext (ano)". Fonte única para o texto clicável da
   * lista e para o título da modal de pré-visualização, para o usuário ver no cabeçalho exatamente
   * o que clicou.
   */
  getSubmissionLabel(sub: OnboardingProcessStageSubmittedDocuments): string {
    const name =
      sub.fileNameWithoutTimestampWithoutExtension || sub.fileNameWithoutTimestamp || 'Arquivo';
    const extension = sub.fileExtension ? `.${sub.fileExtension}` : '';
    const year = sub.year ? ` (${sub.year})` : '';
    return `${name}${extension}${year}`;
  }

  /**
   * Abre a modal compartilhada de pré-visualização (`FilePreviewModalComponent`) para o arquivo
   * clicado, com os mesmos parâmetros usados no wizard (`StepDocumentsComponent.openDocument`) e na
   * tabela anual — ver README de `@shared/components/file-preview-modal`. Sem isso o único caminho
   * para conferir o arquivo daqui era o ícone de download, que tira o usuário da tela.
   *
   * `baseZIndex: 10300` é a faixa oficial de modais; como esta modal já está aberta em 10 300, o
   * `autoZIndex` do PrimeNG incrementa e a pré-visualização abre por cima — o retorno fecha só a
   * pré-visualização e mantém o onboarding atrás.
   */
  isRemoving(sub: OnboardingProcessStageSubmittedDocuments): boolean {
    return !!sub.id && this.removingIds.has(sub.id);
  }

  /**
   * Descarta um arquivo enviado direto por esta modal, reusando o mesmo fluxo do wizard
   * (`StepDocumentsComponent.onFileRemoved`): confirmação `danger` → `deleteSubmittedDocument` →
   * toast, com a mensagem real do backend em caso de erro. Antes daqui só era possível remover
   * entrando no onboarding da empresa.
   *
   * Após o sucesso a linha sai da lista **localmente** (`splice` em `doc.submittedDocuments`), sem
   * refazer o GET: barra de progresso, contadores e chips desta modal derivam de `data`, então
   * recalculam sozinhos, e a seção que o usuário estava lendo continua aberta. O único campo que não
   * acompanha é o `stage.isCompleted`, que vem calculado do backend e só se atualiza ao reabrir —
   * refazer o GET aqui custaria colapsar a sanfona e reabrir o spinner a cada remoção.
   */
  removeSubmission(
    doc: OnboardingProcessStageRequiredDocuments,
    sub: OnboardingProcessStageSubmittedDocuments
  ): void {
    const id = sub.id;
    if (!id || this.removingIds.has(id)) return;

    this.confirmationService
      .confirm('Remover arquivo', 'Confirma a remoção definitiva deste arquivo?', 'danger')
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.removingIds.add(id);

        this.onboardingService.deleteSubmittedDocument(id).subscribe({
          next: () => {
            const list = doc.submittedDocuments ?? [];
            const index = list.indexOf(sub);
            if (index !== -1) list.splice(index, 1);

            this.removingIds.delete(id);
            this.toastService.showToastSuccess('Arquivo removido com sucesso');
          },
          error: (err: unknown) => {
            this.removingIds.delete(id);
            this.toastService.showToastError(
              'Falha ao remover o documento',
              extractApiErrorMessage(err, 'Erro ao remover o arquivo. Tente novamente.')
            );
          },
        });
      });
  }

  openSubmissionPreview(sub: OnboardingProcessStageSubmittedDocuments): void {
    const fileUrl = sub.documentLink;
    if (!fileUrl) return;

    const fileName = this.getSubmissionLabel(sub);
    const extension =
      sub.fileExtension || extractFileExtension(fileUrl) || extractFileExtension(fileName);

    const data: FilePreviewData = {
      fileName,
      fileUrl,
      previewType: resolveFilePreviewType(extension),
    };

    this.dialogService.open(FilePreviewModalComponent, {
      data,
      showHeader: false,
      modal: true,
      dismissableMask: true,
      width: '95vw',
      height: '95vh',
      styleClass: 'file-preview-dialog',
      contentStyle: { padding: '0', height: '100%', overflow: 'hidden' },
      baseZIndex: 10300,
    });
  }

  isSectionOpen(index: number): boolean {
    return this.openSections.has(index);
  }

  toggleSection(index: number): void {
    if (this.openSections.has(index)) {
      this.openSections.delete(index);
    } else {
      this.openSections.add(index);
    }
    this.openSections = new Set(this.openSections);
  }

  private get requiredStages(): OnboardingProcessStage[] {
    return (this.data?.onboardingProcess?.stages ?? []).filter(s => !this.isStageDesirable(s));
  }

  get progress(): number {
    const stages = this.requiredStages;
    if (!stages.length) return 0;
    return Math.round(
      (stages.filter(s => this.isStageEffectivelyCompleted(s)).length / stages.length) * 100
    );
  }

  get completedCount(): number {
    return this.requiredStages.filter(s => this.isStageEffectivelyCompleted(s)).length;
  }

  get totalStages(): number {
    return this.requiredStages.length;
  }

  isStageDesirable(stage: OnboardingProcessStage): boolean {
    if (stage.name === 'CompanyInformation' || stage.name === 'Contacts') return false;
    return !(stage.requiredDocuments?.some(d => d.isRequired) ?? false);
  }

  isStageEffectivelyCompleted(stage: OnboardingProcessStage): boolean {
    if (this.isStageDesirable(stage)) {
      return (
        stage.requiredDocuments?.some(doc =>
          doc.submittedDocuments?.some(sd => sd.statusAnalysisDocuments !== 'Rejected')
        ) ?? false
      );
    }
    if (!stage.isCompleted) return false;
    if (stage.name === 'CompanyInformation' || stage.name === 'Contacts') return true;
    return stage.requiredDocuments?.some(doc => (doc.submittedDocuments?.length ?? 0) > 0) ?? false;
  }

  getStageLabel(stageName: string): string {
    return STAGE_LABELS[stageName] ?? stageName;
  }

  getDocumentDisplayName(doc: OnboardingProcessStageRequiredDocuments): string {
    return DOCUMENT_LABELS[doc.name] ?? DOCUMENT_LABELS[doc.category] ?? doc.name;
  }

  getSubmissionStatusLabel(status: string): string {
    switch (status) {
      case 'Approved':
        return 'Aprovado';
      case 'Rejected':
        return 'Rejeitado';
      default:
        return 'Em análise';
    }
  }

  getSubmissionStatusKey(status: string): string {
    switch (status) {
      case 'Approved':
        return 'approved';
      case 'Rejected':
        return 'rejected';
      default:
        return 'pending';
    }
  }

  getValidSubmissionsCount(doc: OnboardingProcessStageRequiredDocuments): number {
    return (
      doc.submittedDocuments?.filter(s => s.statusAnalysisDocuments !== 'Rejected').length ?? 0
    );
  }

  get hasDownloadableDocuments(): boolean {
    return (this.data?.onboardingProcess?.stages ?? [])
      .filter(s => s.name !== 'CompanyInformation' && s.name !== 'Contacts')
      .some(s =>
        s.requiredDocuments?.some(d => d.submittedDocuments?.some(sd => !!sd.documentLink))
      );
  }

  async downloadAllDocuments(): Promise<void> {
    if (!this.data || this.isDownloading) return;

    const stages = (this.data.onboardingProcess?.stages ?? []).filter(
      s => s.name !== 'CompanyInformation' && s.name !== 'Contacts'
    );

    const rawFiles: Array<{ folder: string; filename: string; url: string }> = [];

    for (const stage of stages) {
      const folder = STAGE_LABELS[stage.name ?? ''] ?? stage.name ?? 'Outros';
      for (const doc of stage.requiredDocuments ?? []) {
        for (const sub of doc.submittedDocuments ?? []) {
          if (!sub.documentLink) continue;
          const ext = sub.fileExtension ? `.${sub.fileExtension}` : '';
          const base =
            sub.fileNameWithoutTimestampWithoutExtension ||
            (sub.fileNameWithoutTimestamp
              ? sub.fileNameWithoutTimestamp.replace(/\.[^/.]+$/, '')
              : 'Arquivo');
          const year = sub.year ? ` (${sub.year})` : '';
          rawFiles.push({ folder, filename: `${base}${year}${ext}`, url: sub.documentLink });
        }
      }
    }

    // Deduplica nomes dentro da mesma pasta
    const seenNames = new Map<string, number>();
    const files = rawFiles.map(f => {
      const key = `${f.folder}/${f.filename}`;
      const count = (seenNames.get(key) ?? 0) + 1;
      seenNames.set(key, count);
      if (count === 1) return { ...f, finalName: f.filename };
      const dotIdx = f.filename.lastIndexOf('.');
      const finalName =
        dotIdx > 0
          ? `${f.filename.slice(0, dotIdx)} (${count})${f.filename.slice(dotIdx)}`
          : `${f.filename} (${count})`;
      return { ...f, finalName };
    });

    this.isDownloading = true;
    try {
      const zip = new JSZip();
      await Promise.all(
        files.map(async file => {
          try {
            const response = await fetch(file.url);
            if (!response.ok) return;
            const blob = await response.blob();
            zip.folder(file.folder)!.file(file.finalName, blob);
          } catch {
            // Falha individual ignorada — os demais arquivos continuam
          }
        })
      );
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const companyName = this.sanitizeFilename(
        this.data!.corporateName || this.data!.tradeName || 'Empresa'
      );
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(zipBlob);
      anchor.download = `${companyName} - Onboarding.zip`;
      anchor.click();
      URL.revokeObjectURL(anchor.href);
    } catch {
      this.toastService.showToastError('Erro ao gerar o arquivo ZIP. Tente novamente.');
    } finally {
      this.isDownloading = false;
    }
  }

  private sanitizeFilename(name: string): string {
    return name.replace(/[<>:"/\\|?*\x00-\x1f]/g, '').trim();
  }

  private scrollModalContentToTop(): void {
    this.getScrollableContentElement()?.scrollTo({ top: 0, behavior: 'auto' });
  }

  private getScrollableContentElement(): HTMLElement | null {
    return document.querySelector(
      '.dialog-key-onboarding-v2-view .p-dialog-content'
    ) as HTMLElement | null;
  }
}
